import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import LS from '../styles/layoutStyled';
import CS from '../styles/componentStyled';
import styled from 'styled-components';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  blankDefaultMsgRecoil,
  errorMsgRecoil,
  confirmMsgRecoil,
  privacyPolicyStateRecoil,
} from '../atom/ModalAtom';
import Header2 from '../component/Header2';
import { SignUpDto } from '../model/user.dto';
import { authAPI } from '../api/axiosIndex';
import PrivacyPolicy from '../modal/privacy/PrivacyPolicy';
import CheckBox from '../component/CheckBox';
import { Icon } from '../component/Icon';

const SignUp = () => {
  const blankMsg = useRecoilValue(blankDefaultMsgRecoil);
  const setError = useSetRecoilState(errorMsgRecoil);
  const setConfirm = useSetRecoilState(confirmMsgRecoil);
  const [privacyPolicyState, setPrivacyPolicyState] = useRecoilState(
    privacyPolicyStateRecoil,
  );

  const navigate = useNavigate();

  const [id, setId] = useState('');
  const [idCertification, setIdCertification] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [phoneNum, setPhoneNum] = useState('');
  const [phoneNumSend, setPhoneNumSend] = useState(false);
  const [phoneNumCertificationId, setPhoneNumCertificationId] = useState('');
  const [certificationNum, setCertificationNum] = useState('');
  const [phoneNumCertification, setPhoneNumCertification] = useState(false);
  const [userNumber, setUserNumber] = useState('');
  const [otherText, setOtherText] = useState('');
  const [otherTextLength, setOtherTextLength] = useState(0);
  const [nickName, setNickName] = useState('');
  const [nickNameCertification, setNickNameCertification] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(180);
  const [timerActive, setTimerActive] = useState<boolean>(false);
  const [checkIdArray, setCheckIdArray] = useState<string[]>([]);
  const [privacyPolicyCheck, setPrivacyPolicyCheck] = useState<boolean>(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (timerActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => {
      clearInterval(timer);
    };
  }, [timerActive, timeLeft]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberRegex = /^010\d{8}$/;
    return phoneNumberRegex.test(number);
  };

  const handleIdCheck = async () => {
    if (id === '') {
      setError('아이디를 입력해 주세요.');
      return;
    }

    try {
      await authAPI.idCheck(id);
      setConfirm('사용 가능한 아이디입니다.');
      setIdCertification(true);
    } catch {
      setError('중복된 아이디입니다.');
    }
  };

  const handlePhoneNumCertificationSend = async () => {
    if (phoneNum === '') {
      setError('휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!validatePhoneNumber(phoneNum)) {
      setError('휴대폰 번호 형식이 올바르지 않습니다.');
      return;
    }

    try {
      const res = await authAPI.postPhoneNumCertification(phoneNum);
      setPhoneNumCertificationId(res.data.uuid);
      setConfirm('인증번호 발송이 완료되었습니다.');
      setPhoneNumSend(true);
      setTimeLeft(180);
      setTimerActive(true);
    } catch (err: any) {
      setError('해당 휴대폰 번호로 가입된 아이디가 존재합니다.');
    }
  };

  const handleCertificationNumconfirm = async () => {
    if (certificationNum === '') {
      setError('인증 번호를 입력해 주세요.');
      return;
    }

    if (timeLeft === 0) {
      setTimerActive(false);
      setError(
        '인증번호 유효시간이 만료되었습니다.\n재발송 후 시도하여 주세요.',
      );
      return;
    }

    try {
      await authAPI.postCertificationNumConfirm(
        phoneNumCertificationId,
        certificationNum,
      );
      setConfirm('본인 인증이 완료되었습니다.');
      setPhoneNumCertification(true);
    } catch (err: any) {
      setError('인증 번호가 일치하지 않습니다.');
    }
  };

  const handleNicknameCheck = async () => {
    if (nickName === '') {
      setError('닉네임을 입력해 주세요.');
      return;
    }

    try {
      await authAPI.nicknameCheck(nickName);
      setConfirm('사용 가능한 닉네임입니다.');
      setNickNameCertification(true);
    } catch (err: any) {
      setError('중복된 닉네임입니다.');
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      id === '' ||
      password === '' ||
      passwordConfirm === '' ||
      phoneNum === '' ||
      userNumber === '' ||
      nickName === ''
    ) {
      setError(blankMsg);
      return;
    }

    if (!idCertification) {
      setError('아이디 중복확인을 해주세요.');
      return;
    }

    if (password.length < 8 || !password.match(/^(?=.*[a-zA-Z])(?=.*\d).*$/)) {
      setError('영문과 숫자를 조합하여 8~20자로 입력해 주세요.');
      return;
    }

    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    // if (!phoneNumCertification) {
    //   setError('본인인증이 완료되지 않았습니다.');
    //   return;
    // }

    if (!nickNameCertification) {
      setError('닉네임 중복확인을 해주세요.');
      return;
    }

    if (!privacyPolicyCheck) {
      setError('개인정보 수집 및 이용약관에 동의해주세요.');
      return;
    }

    const formData: SignUpDto = {
      passid: id,
      password: password,
      phone: phoneNum,
      userNumber: userNumber,
      otherText: otherText,
      nickname: nickName,
      privacyPolicyAgreed: privacyPolicyCheck,
    };

    try {
      await authAPI.signUp(formData);
      navigate('/login');
      setConfirm('회원가입이 완료되었습니다.');
    } catch {
      setError('회원가입에 실패하였습니다.');
    }
  };

  useEffect(() => {
    if (checkIdArray.includes('privacyPolicy')) {
      setPrivacyPolicyCheck(true);
    } else {
      setPrivacyPolicyCheck(false);
    }
  }, [checkIdArray]);

  return (
    <>
      <Header2 title={'회원가입'} />
      <form onSubmit={handleSignUp}>
        <LS.LoginCntLayout>
          <CS.LoginInputWrap>
            <CS.LoginInputType>
              <h1>
                아이디
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="text"
                  placeholder="아이디 입력"
                  value={id}
                  onChange={(e) => {
                    setId(e.target.value);
                    setIdCertification(false);
                  }}
                />
                <button
                  type="button"
                  onClick={handleIdCheck}
                  disabled={idCertification}
                >
                  {idCertification ? '완료' : '중복확인'}
                </button>
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                비밀번호
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="password"
                  placeholder="비밀번호 입력 (영문 숫자 조합 8~20자)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                비밀번호 확인
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="password"
                  placeholder="비밀번호 입력 확인"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                />
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                휴대폰 번호
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="text"
                  placeholder="휴대폰 번호 입력 (-자 제외)"
                  value={phoneNum}
                  maxLength={11}
                  disabled={phoneNumCertification}
                  onChange={(e) => {
                    setPhoneNum(e.target.value);
                    setCertificationNum('');
                    setPhoneNumSend(false);
                  }}
                />
                {!phoneNumCertification && (
                  <button
                    type="button"
                    onClick={handlePhoneNumCertificationSend}
                    className={phoneNumSend ? 'retry' : ''}
                  >
                    {phoneNumSend ? '재발송' : '본인인증'}
                  </button>
                )}
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                인증 번호
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="text"
                  placeholder="인증번호"
                  value={certificationNum}
                  disabled={phoneNumCertification}
                  onChange={(e) => {
                    setCertificationNum(e.target.value);
                  }}
                />
                {!phoneNumCertification && (
                  <div className="right-box">
                    <span
                      className={
                        timeLeft === 0 ? 'time-text timezero' : 'time-text'
                      }
                    >
                      {formatTime(timeLeft)}
                    </span>
                    <button
                      type="button"
                      onClick={handleCertificationNumconfirm}
                      disabled={phoneNumCertification}
                    >
                      {phoneNumCertification ? '인증완료' : '확인'}
                    </button>
                  </div>
                )}
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                번호
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="text"
                  placeholder="사번 등 입력"
                  value={userNumber}
                  onChange={(e) => setUserNumber(e.target.value)}
                />
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>기타사항({otherTextLength}/15)</h1>
              <div>
                <input
                  type="text"
                  placeholder="기타사항 입력"
                  value={otherText}
                  onChange={(e) => {
                    if (e.target.value.length > 15) {
                      setError('기타사항은 15자 이하로 입력해 주세요.');
                      return;
                    } else {
                      setOtherText(e.target.value);
                      setOtherTextLength(e.target.value.length);
                    }
                  }}
                />
              </div>
            </CS.LoginInputType>
            <CS.LoginInputType>
              <h1>
                닉네임
                <span className="img-box">
                  <Icon name="StarIcon" size="100%" color="#FF5151" />
                </span>
              </h1>
              <div>
                <input
                  type="text"
                  placeholder="닉네임 입력"
                  value={nickName}
                  onChange={(e) => {
                    setNickName(e.target.value);
                    setNickNameCertification(false);
                  }}
                />
                <button
                  type="button"
                  onClick={handleNicknameCheck}
                  disabled={nickNameCertification}
                >
                  {nickNameCertification ? '완료' : '중복확인'}
                </button>
              </div>
            </CS.LoginInputType>
            <PrivacyBox>
              <CheckBox
                checkIdArray={checkIdArray}
                setCheckIdArray={setCheckIdArray}
                checkValue="privacyPolicy"
              />
              <button onClick={() => setPrivacyPolicyState(true)}>
                개인정보 수집 및 이용약관
                <span className="img-box">
                  <Icon name="ArrowNextIcon" size="100%" color="#949494" />
                </span>
              </button>
            </PrivacyBox>
          </CS.LoginInputWrap>
        </LS.LoginCntLayout>
        <LS.LoginBtnLayout>
          <CS.Btn1Type1 type="submit">회원가입 완료</CS.Btn1Type1>
        </LS.LoginBtnLayout>
      </form>
      {privacyPolicyState && <PrivacyPolicy />}
    </>
  );
};

export default SignUp;

const PrivacyBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0;

  > button {
    font-size: 14px;
    color: #777;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 2px;
    > .img-box {
      width: 16px;
    }
  }
`;
